import { AlertTriangle, Trash2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { listComprobantesByOperacion } from '@/shared/api/controllers/comprobantePagos';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';

/**
 * Pre-submit delete confirmation. The backend decides whether the delete
 * runs directly or creates an authorization request based on payment
 * state — we don't know the outcome up-front, so the dialog copy tells
 * the user *what might happen*:
 *
 *   - For a cotización: straight anulación.
 *   - For a venta: soft-cancel if no real payments exist, otherwise a
 *     request is routed to admin.
 */
export function ConfirmarEliminarDialog({
  open,
  tipo,
  nr,
  cliente,
  operacionId,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  tipo: 'Cotizacion' | 'Venta';
  nr: string;
  cliente: string;
  /** SP id de la venta — habilita el aviso de facturas ya emitidas. */
  operacionId?: string | number | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isCotizacion = tipo === 'Cotizacion';
  // Con Facturación Previa una venta puede tener facturas emitidas aunque
  // todavía no se haya cobrado nada. Eliminarla deja esos comprobantes vivos
  // ante ARCA sin venta detrás, y sólo una Nota de Crédito los revierte — así
  // que el operador tiene que verlo ANTES de confirmar. Lookup on-demand: sólo
  // corre con el diálogo abierto, no en cada fila de la lista.
  const facturadasQ = useQuery({
    queryKey: ['pagos', 'comprobantes-full', String(operacionId ?? '')],
    queryFn: () => listComprobantesByOperacion(operacionId!),
    enabled: open && !isCotizacion && operacionId != null,
    staleTime: 30_000,
  });
  const facturasVivas = (facturadasQ.data ?? []).filter(
    (c) => c.fields.Facturado_CP === 'SI' && (c.fields.Status_P ?? '') !== 'Anulado',
  );
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Eliminar {isCotizacion ? 'cotización' : 'venta'}</DialogTitle>
              <DialogDescription>
                Vas a eliminar la {isCotizacion ? 'cotización' : 'venta'} Nº{' '}
                <span className="font-semibold text-foreground">{nr}</span> de{' '}
                <span className="font-medium text-foreground">{cliente}</span>.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-2 text-sm text-muted-foreground">
          {isCotizacion ? (
            <p>
              La cotización quedará anulada junto con sus detalles, descuentos
              y solicitudes de aprobación pendientes. Esta acción no se puede
              deshacer desde acá.
            </p>
          ) : (
            <>
              <p>
                Si la venta no tiene pagos registrados se anulará de forma
                directa.
              </p>
              <p>
                Si ya tiene pagos cargados, se enviará una{' '}
                <span className="font-medium text-foreground">
                  solicitud de autorización
                </span>{' '}
                al administrador y la eliminación quedará pendiente de
                aprobación.
              </p>
            </>
          )}
          {facturasVivas.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
              <p className="font-semibold uppercase tracking-wide text-amber-800">
                {facturasVivas.length === 1
                  ? 'Esta venta tiene una factura emitida'
                  : `Esta venta tiene ${facturasVivas.length} facturas emitidas`}
              </p>
              <p className="mt-1">
                {facturasVivas
                  .map((c) => c.fields.NroFactura_CP)
                  .filter(Boolean)
                  .join(', ')}
                {facturasVivas.some((c) => c.fields.NroFactura_CP) ? '. ' : ''}
                Eliminar la venta <strong>no da de baja las facturas</strong>:
                siguen vigentes ante ARCA y sólo una{' '}
                <strong>Nota de Crédito</strong> las revierte. Por eso la
                eliminación pide aprobación; una vez aprobada, quedan en el aviso
                de la pantalla principal hasta que se emita la NC.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Volver
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            className="gap-2 bg-rose-600 text-white hover:bg-rose-700"
          >
            <Trash2 className="h-4 w-4" aria-hidden /> Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
